<template>
  <MGuidedCheckoutStep
    :step-number="stepNumber"
    title="Wähle die Leiste."
    :description="stepDescription"
  >
    <div class="m-gc-step-skirt__filters">
      <button
        v-for="filter in availableFilters"
        :key="filter.id"
        type="button"
        class="m-gc-step-skirt__filter"
        :class="{ 'm-gc-step-skirt__filter--active': activeFilter === filter.id }"
        @click="activeFilter = filter.id"
      >
        {{ filter.label }}
      </button>
    </div>

    <div class="m-gc-step-skirt__items product-boxes-items">
      <button
        v-if="activeFilter === 'none'"
        type="button"
        class="m-gc-step-skirt__item"
        :class="{ 'm-gc-step-skirt__item--active': !selectedLinkId }"
        @click="clearSelection"
      >
        <div
          class="m-gc-step-skirt__item-thumb m-gc-step-skirt__item-thumb--empty"
          aria-hidden="true"
        />
        <div class="m-gc-step-skirt__item-content">
          <strong>Keine Leiste</strong>
          <AProductBasePrice
            :regular="0"
            :factor="1"
            unit="m"
          />
        </div>
      </button>

      <template v-if="activeFilter !== 'none'">
        <button
          v-for="link in visibleLinks"
          :key="link.id"
          type="button"
          class="m-gc-step-skirt__item"
          :class="{ 'm-gc-step-skirt__item--active': selectedLinkId === link.id }"
          @click="selectLink(link)"
        >
          <SfImage
            v-if="link.product"
            :src="getThumbnailForProduct(link.product)"
            :alt="link.product.name"
            width="72"
            height="72"
          />
          <div class="m-gc-step-skirt__item-content">
            <strong>{{ link.product && link.product.name }}</strong>
            <AProductBasePrice
              v-if="link.product"
              :regular="link.price < link.product.final_price_incl_tax ? link.product.final_price_incl_tax : link.price"
              :special="link.price < link.product.final_price_incl_tax ? link.price : null"
              :factor="link.product.baseprice_product_amount"
              :unit="link.product.baseprice_product_unit"
            />
          </div>
        </button>
      </template>
    </div>
  </MGuidedCheckoutStep>
</template>

<script>
import { SfImage } from '@storefront-ui/vue'
import { getThumbnailForProduct } from '@vue-storefront/core/modules/cart/helpers'
import MGuidedCheckoutStep from 'theme/components/molecules/guided-checkout/m-guided-checkout-step'
import AProductBasePrice from 'theme/components/atoms/a-product-base-price'
import guidedCheckoutConfig from 'theme/config/guided-checkout'
import {
  clearBundleOptionSelection,
  filterBundleLinksForPreset,
  getSkirtBundleOption,
  getSkirtFilterForPreset,
  productLinkMatchesFilter,
  selectBundleOptionLink
} from 'theme/helpers/guided-checkout'
import { getSelectedBundleOptionValue } from 'theme/helpers/bundle'

export default {
  name: 'MGcStepSkirt',
  components: {
    MGuidedCheckoutStep,
    SfImage,
    AProductBasePrice
  },
  props: {
    stepNumber: {
      type: [Number, String],
      default: 3
    },
    product: {
      type: Object,
      required: true
    },
    bundleOptionsComponent: {
      type: Object,
      default: null
    },
    packagePreset: {
      type: String,
      default: 'standard'
    }
  },
  data () {
    return {
      activeFilter: getSkirtFilterForPreset('standard'),
      filters: guidedCheckoutConfig.skirtFilterGroups
    }
  },
  watch: {
    packagePreset: {
      immediate: true,
      handler (preset) {
        this.activeFilter = getSkirtFilterForPreset(preset)
        this.ensureActiveFilterAvailable()
      }
    },
    availableFilters () {
      this.ensureActiveFilterAvailable()
    }
  },
  computed: {
    skirtOption () {
      return getSkirtBundleOption(this.product)
    },
    presetLinks () {
      if (!this.skirtOption) return []
      return filterBundleLinksForPreset(this.skirtOption.product_links || [], this.packagePreset)
    },
    stepDescription () {
      if (this.packagePreset === 'einzelprodukt') {
        return 'Beim Einzelboden kannst du eine Leiste frei wählen oder darauf verzichten.'
      }
      if (this.packagePreset === 'premium') {
        return 'Im Premium Set sind nur Aufpreis-Leisten wählbar. Eine passende Auswahl ist vorausgewählt.'
      }
      return 'Im Standard Set sind nur die kostenlosen Standard-Leisten wählbar. Eine passende Auswahl ist vorausgewählt.'
    },
    selectedLinkId () {
      if (!this.skirtOption) return null
      const selected = getSelectedBundleOptionValue(this.product, this.skirtOption)
      return selected ? selected.id : null
    },
    availableFilters () {
      return this.filters.filter(filter => {
        if (filter.id === 'none') return this.packagePreset === 'einzelprodukt'
        if (!this.presetLinks.length) return false

        return this.presetLinks.some(link => {
          return productLinkMatchesFilter(link, filter.id, this.filters)
        })
      })
    },
    visibleLinks () {
      if (!this.presetLinks.length || this.activeFilter === 'none') return []

      return this.presetLinks.filter(link => {
        return productLinkMatchesFilter(link, this.activeFilter, this.filters)
      })
    }
  },
  methods: {
    getThumbnailForProduct,
    ensureActiveFilterAvailable () {
      if (this.availableFilters.some(filter => filter.id === this.activeFilter)) return
      this.activeFilter = (this.availableFilters[0] && this.availableFilters[0].id) || 'none'
    },
    selectLink (link) {
      selectBundleOptionLink(this.bundleOptionsComponent, this.skirtOption, link)
      this.$emit('changed')
    },
    clearSelection () {
      clearBundleOptionSelection(this.bundleOptionsComponent, this.skirtOption)
      this.$emit('changed')
    }
  }
}
</script>

<style lang="scss" scoped>
.m-gc-step-skirt {
  &__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  &__filter {
    height: auto;
    min-height: 36px;
    padding: 0.4rem 0.75rem;
    border: 1px solid var(--c-light);
    border-radius: 999px;
    background: var(--c-white);
    cursor: pointer;
    white-space: nowrap;
    transition: var(--gc-transition);

    &--active {
      border-color: var(--c-primary);
      color: var(--c-primary);
      font-weight: 700;
    }
  }

  &__items {
    display: grid;
    gap: 0.75rem;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    width: 100%;
    height: auto;
    min-height: 0;
    padding: 0.75rem;
    border: 1px solid var(--c-light);
    border-radius: 4px;
    background: var(--c-white);
    text-align: left;
    cursor: pointer;
    white-space: normal;
    overflow: hidden;
    transition: var(--gc-transition);

    &--active {
      border-color: var(--c-primary);
      box-shadow: 0 0 0 1px var(--c-primary);
    }

    ::v-deep .sf-image,
    .m-gc-step-skirt__item-thumb {
      flex: 0 0 72px;
      width: 72px;
      height: 72px;
      max-width: 72px;
      overflow: hidden;
      border-radius: 4px;
      background: var(--c-light);

      img {
        width: 72px;
        height: 72px;
        object-fit: cover;
      }
    }

    .m-gc-step-skirt__item-thumb--empty {
      box-shadow: inset 0 0 0 1px var(--c-light);
    }

    strong {
      display: block;
      margin-bottom: 0.35rem;
      line-height: 1.3;
      font-weight: 700;
      white-space: normal;
      overflow-wrap: anywhere;
    }

    span {
      color: var(--c-dark-variant);
      font-size: 0.875rem;
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }

  &__item-content {
    flex: 1;
    min-width: 0;
  }
}
</style>
